import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, setCsrfToken } from '../api/client.js';
import { useT } from '../i18n/index.js';
import { useSession } from '../store/session.js';
import { Banner, Button, Card, Field } from '../components/ui.js';
import { BrandMark } from '../components/icons.js';

/**
 * ACCESO LOCAL.
 *
 * El cliente final entra por **SSO de Besbello**; esta pantalla es para el
 * PERSONAL (taller, comercial, administración), que tiene cuenta local con MFA
 * en los roles administrativos.
 *
 * En desarrollo también sirve para entrar como cliente de prueba sin montar el
 * SSO entero: `npm run seed` crea las cuentas y las imprime por consola.
 */
export function LoginPage() {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const load = useSession((s) => s.load);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const me = await api.staffLogin(email, password, otp || undefined);
      setCsrfToken(me.csrfToken);
      await load();
      navigate(params.get('next') ?? '/');
    } catch (err) {
      // El mensaje es el mismo tanto si el usuario no existe como si la
      // contraseña falla: decir cuál de las dos es regalar media credencial.
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col gap-4 px-4 py-10">
      <div className="flex items-center gap-3">
        <BrandMark size={36} className="text-tinta" />
        <div>
          <p className="font-semibold">{t('app.brand')}</p>
          <p className="text-sm text-tinta-suave">Acceso del personal</p>
        </div>
      </div>

      <Card>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Correo">
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="toque w-full rounded-lg border border-borde px-3 py-2 text-sm"
            />
          </Field>

          <Field label="Contraseña">
            <input
              type="password"
              autoComplete="current-password"
              required
              minLength={12}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="toque w-full rounded-lg border border-borde px-3 py-2 text-sm"
            />
          </Field>

          <Field label="Código de verificación" hint="Solo si tienes MFA activado.">
            <input
              inputMode="numeric"
              pattern="\d{6}"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className="toque w-full rounded-lg border border-borde px-3 py-2 text-sm tabular-nums"
            />
          </Field>

          {error && <Banner tone="error" title={t('app.error')}>{error}</Banner>}

          <Button type="submit" loading={busy} className="w-full">
            Entrar
          </Button>
        </form>
      </Card>

      <p className="text-center text-sm text-tinta-suave">
        ¿Eres cliente?{' '}
        <button
          onClick={() => void useSession.getState().login('/')}
          className="underline"
        >
          {t('nav.login')}
        </button>
      </p>
    </div>
  );
}
